import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'config/axios';
import type { FillAppValues } from 'global/fillapp';

const DEBOUNCE_MS = 2000;
const RETRY_MS = 5000;

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'conflict' | 'error';

export interface UseFillAppAutosaveResult {
    values: FillAppValues;
    status: AutosaveStatus;
    lastSavedAt: string | null;
    setValue: (qid: string, value: unknown) => void;
    flushNow: () => void;
    resetWithServerState: (next: { values: FillAppValues; lastSavedAt: string | null }) => void;
}

interface Args {
    studentFillAppId: number | null;
    initialValues: FillAppValues;
    initialLastSavedAt: string | null;
}

export function useFillAppAutosave({
    studentFillAppId,
    initialValues,
    initialLastSavedAt,
}: Args): UseFillAppAutosaveResult {
    const [values, setValues] = useState<FillAppValues>(initialValues);
    const [status, setStatus] = useState<AutosaveStatus>('idle');
    const [lastSavedAt, setLastSavedAt] = useState<string | null>(initialLastSavedAt);

    const pendingRef = useRef<Record<string, unknown>>({});
    const lastSavedAtRef = useRef<string | null>(initialLastSavedAt);
    const inFlightRef = useRef<boolean>(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const idRef = useRef<number | null>(studentFillAppId);

    useEffect(() => { idRef.current = studentFillAppId; }, [studentFillAppId]);
    useEffect(() => { lastSavedAtRef.current = lastSavedAt; }, [lastSavedAt]);

    const clearDebounce = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    };
    const clearRetry = () => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
    };

    const conflictRef = useRef<boolean>(false);

    const flush = useCallback(async () => {
        clearDebounce();
        const id = idRef.current;
        if (id == null) return;
        if (inFlightRef.current) return;
        if (conflictRef.current) return;
        const updates = pendingRef.current;
        const keys = Object.keys(updates);
        if (keys.length === 0) return;

        inFlightRef.current = true;
        const sending = { ...updates };
        pendingRef.current = {};
        setStatus('saving');

        try {
            const res = await axios.post(`fillapp/student/save/${id}/`, {
                updates: sending,
                lastSavedAt: lastSavedAtRef.current,
            });
            const next = res.data?.lastSavedAt ?? null;
            lastSavedAtRef.current = next;
            setLastSavedAt(next);
            setStatus('saved');
            clearRetry();
        } catch (err: unknown) {
            pendingRef.current = { ...sending, ...pendingRef.current };
            const httpStatus = (err as { response?: { status?: number } })?.response?.status;
            if (httpStatus === 409) {
                conflictRef.current = true;
                setStatus('conflict');
            } else {
                setStatus('error');
                clearRetry();
                retryTimerRef.current = setTimeout(() => { flush(); }, RETRY_MS);
            }
        } finally {
            inFlightRef.current = false;
            if (Object.keys(pendingRef.current).length > 0 && !conflictRef.current) {
                clearDebounce();
                debounceTimerRef.current = setTimeout(() => { flush(); }, DEBOUNCE_MS);
            }
        }
    }, []);

    const setValue = useCallback((qid: string, value: unknown) => {
        setValues(prev => ({ ...prev, [qid]: value }));
        pendingRef.current[qid] = value;
        if (conflictRef.current) return;
        clearDebounce();
        debounceTimerRef.current = setTimeout(() => { flush(); }, DEBOUNCE_MS);
    }, [flush]);

    const flushNow = useCallback(() => {
        if (conflictRef.current) return;
        flush();
    }, [flush]);

    const resetWithServerState = useCallback((next: { values: FillAppValues; lastSavedAt: string | null }) => {
        pendingRef.current = {};
        clearDebounce();
        clearRetry();
        conflictRef.current = false;
        setValues(next.values);
        lastSavedAtRef.current = next.lastSavedAt;
        setLastSavedAt(next.lastSavedAt);
        setStatus('idle');
    }, []);

    // Flush on unmount — best-effort, fire-and-forget.
    useEffect(() => {
        return () => {
            if (Object.keys(pendingRef.current).length === 0) return;
            const id = idRef.current;
            if (id == null) return;
            // Don't await; React unmount is sync. axios call still goes out.
            axios.post(`fillapp/student/save/${id}/`, {
                updates: pendingRef.current,
                lastSavedAt: lastSavedAtRef.current,
            }).catch(() => { /* best-effort */ });
        };
    }, []);

    // Flush on tab close as a safety net.
    useEffect(() => {
        const handler = () => {
            if (Object.keys(pendingRef.current).length === 0) return;
            // Synchronous best effort — we don't know if the browser will
            // honor a regular xhr/fetch here. sendBeacon is the right tool but
            // can't send auth headers, so we accept this is best-effort and
            // rely on debounce/blur catching most edits.
            flush();
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [flush]);

    return {
        values,
        status,
        lastSavedAt,
        setValue,
        flushNow,
        resetWithServerState,
    };
}
